import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, Mail, Phone, Search, Globe, MapPin } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import AddClubDialog from "@/components/AddClubDialog";
import { fetchSports } from "@/lib/sportsCatalog";
import { STATUS_BADGE, cn } from "@/lib/utils";
import { useLang } from "@/i18n";

export default function Clubs() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [openData, setOpenData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [sportLabels, setSportLabels] = useState({});

  useEffect(() => {
    fetchSports().then((list) => {
      const m = {};
      list.forEach((s) => { m[s.id] = s.label; });
      setSportLabels(m);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const r = await api.get("/clubs", { params: q ? { q } : {} });
    setClubs(r.data);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (openId) api.get(`/clubs/${openId}`).then((r) => setOpenData(r.data));
    else setOpenData(null);
  }, [openId]);

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t("clubs.title")}</div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mt-1">{t("clubs.count", { n: clubs.length })}</h1>
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:flex-initial">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("clubs.search")} className="pl-8 w-full md:w-64" data-testid="clubs-search-input" />
          </div>
          <Button onClick={() => setAddOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="clubs-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Club
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {clubs.map((c) => (
          <Card
            key={c.id}
            className="bg-surface hover:border-muted-foreground/40 cursor-pointer transition-colors"
            onClick={() => navigate(`/clubs/${c.id}`)}
            data-testid={`club-card-${c.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                {c.logo_url ? (
                  <img
                    src={`${API}/clubs/${c.id}/logo`}
                    alt=""
                    className="h-10 w-10 rounded-md object-contain bg-secondary border border-border"
                    data-testid={`club-logo-${c.id}`}
                  />
                ) : (
                  <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center">
                    <Building2 className="h-5 w-5" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[c.league, c.city || c.country].filter(Boolean).join(" · ")}
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    {c.sport && (
                      <Badge variant="outline" className="text-[10px] border-border text-muted-foreground" data-testid={`club-sport-${c.id}`}>
                        {sportLabels[c.sport] || c.sport}
                      </Badge>
                    )}
                    {c.contact_name && <span className="text-xs text-muted-foreground truncate">{t("clubs.contact")} {c.contact_name}</span>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {clubs.length === 0 && <div className="text-sm text-muted-foreground col-span-full text-center py-12">{t("clubs.empty")}</div>}
      </div>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto scroll-thin" data-testid="club-sheet">
          {!openData && <SheetTitle className="sr-only">Club</SheetTitle>}
          {openData && (
            <>
              <SheetHeader className="pr-10">
                <div className="flex items-center gap-3">
                  {openData.club.logo_url && (
                    <img src={`${API}/clubs/${openData.club.id}/logo`} alt="" className="h-12 w-12 rounded-lg object-contain bg-secondary border border-border" />
                  )}
                  <div className="min-w-0">
                    <SheetTitle className="text-2xl tracking-tight truncate">{openData.club.name}</SheetTitle>
                    <SheetDescription>
                      {[sportLabels[openData.club.sport], openData.club.league, [openData.club.city, openData.club.country].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>
              {(openData.club.venue || openData.club.website) && (
                <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                  {openData.club.venue && (
                    <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {openData.club.venue}</div>
                  )}
                  {openData.club.website && (
                    <a href={openData.club.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-foreground" data-testid="club-website-link">
                      <Globe className="h-3.5 w-3.5" /> {openData.club.website}
                    </a>
                  )}
                </div>
              )}
              {(openData.club.contact_email || openData.club.contact_phone) && (
                <div className="mt-4 rounded-md border border-border bg-surface p-3 space-y-2 text-sm">
                  {openData.club.contact_name && (
                    <div className="font-medium">
                      {openData.club.contact_name}
                      {openData.club.contact_role && <span className="text-muted-foreground font-normal"> — {openData.club.contact_role}</span>}
                    </div>
                  )}
                  {openData.club.contact_email && (
                    <a href={`mailto:${openData.club.contact_email}`} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="club-contact-email">
                      <Mail className="h-3.5 w-3.5" /> {openData.club.contact_email}
                    </a>
                  )}
                  {openData.club.contact_phone && (
                    <a href={`tel:${openData.club.contact_phone}`} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="club-contact-phone">
                      <Phone className="h-3.5 w-3.5" /> {openData.club.contact_phone}
                    </a>
                  )}
                </div>
              )}
              <div className="mt-6">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t("clubs.playersSent", { n: openData.players.length })}</div>
                <div className="space-y-2">
                  {openData.players.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 rounded-md border border-border bg-background p-2.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{p.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{p.position}</div>
                      </div>
                      <Badge variant="outline" className={cn("text-[10px]", STATUS_BADGE[p.status])}>{p.status}</Badge>
                    </div>
                  ))}
                  {openData.players.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">{t("clubs.noPlayersSent")}</div>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AddClubDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
    </div>
  );
}
